import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, type DropResult } from "react-beautiful-dnd";
import { commitLocalUpdate, commitMutation, graphql, useFragment } from "react-relay";
import { useLocation } from "react-router";

import {
	faArrowToTop,
	faCalendar,
	faCalendarCircleUser,
	faClock,
	faDoorClosed,
	faEllipsisV,
	faHandshake,
	faHashtag,
	faLock,
	faPlus,
	faVideo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faUserFriends } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { showFeatureModal } from "~/billing";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import MenuLink, { type MenuLinkProps } from "~/nav/panels/Home/Links/MenuLink";
import AddToLibraryMenu from "~/recordings/AddToLibraryMenu";
import { EditChannelByIdModal } from "~/recordings/CreateEditChannelModal/CreateEditChannelModal";
import { useFetchKeyForEvent } from "~/recordings/events";
import { useChannelsRefetchEventDispatcher, useLeaveChannel, useOpenChannelsModal } from "~/recordings/LibraryHooks";
import { useIsMyTeamPseudoChannelEnabled } from "~/recordings/myTeamPseudoPage/featureFlag";
import RecordWithUrlButton from "~/recordings/RecordWithUrlButton";
import useIsUploadsEnabled from "~/recordings/uploads/useIsUploadsEnabled";
import { Button, toast, useModals } from "~/ui";
import InlineLabel from "~/ui/labels/InlineLabel";
import { IconButton } from "~/ui/legacyFellowIndex";
import { ButtonLink } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { PaywalledPrimaryNavLink, PrimaryNavLink } from "../shared";
import PanelSection from "../shared/PanelSection";

import type { LibraryPanelChannelLink_viewer$key } from "~/__generated__/LibraryPanelChannelLink_viewer.graphql";
import type { LibraryPanelQuery as QueryType } from "~/__generated__/LibraryPanelQuery.graphql";
import type {
	ChangedChannels,
	LibraryPanelReorderChannelMutation,
} from "~/__generated__/LibraryPanelReorderChannelMutation.graphql";

import panelStyles from "../shared/NavPanel.module.css";
import panelSectionStyles from "../shared/PanelSection.module.css";
import styles from "./LibraryPanel.module.css";

const reorderChannelMutation = graphql`
	mutation LibraryPanelReorderChannelMutation($changedChannels: [ChangedChannels]!) {
		reorderChannel(changedChannels: $changedChannels) {
			ok
		}
	}
`;

type Channel = QueryType["response"]["viewer"]["channels"][number] & { order?: number };

type WebsocketMessage = {
	type: string;
	data: {
		channelId: string;
		lastPostedAt: string;
	};
};

function ChannelLink(
	props: Omit<MenuLinkProps, "to" | "innerRef" | "activeClassName"> & {
		viewer: LibraryPanelChannelLink_viewer$key;
		channel: Channel;
		innerRef?: React.Ref<HTMLAnchorElement>;
	},
) {
	const { viewer: viewerKey, channel, innerRef, ...restProps } = props;
	const viewer = useFragment(
		graphql`
			fragment LibraryPanelChannelLink_viewer on Viewer {
				...LibraryHooksUseLeaveChannel_viewer
				account {
					billingAccount {
						canUseChannels: hasAccessToFeature(feature: channels)
					}
				}
			}
		`,
		viewerKey,
	);

	const { canUseChannels } = viewer.account.billingAccount;

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const modals = useModals();
	const location = useLocation();
	const shouldRedirect = Boolean(channel.isPrivate && location.pathname.includes(`library/c/${channel.id}/`));
	const refetchChannels = useChannelsRefetchEventDispatcher();

	const openChannelDetail = React.useCallback(() => {
		if (!canUseChannels) return showFeatureModal("channels");

		dispatchAnalyticsTrack("Open edit channel modal", { location: "Library Panel" });

		modals.open(({ close }) => (
			<EditChannelByIdModal onClose={close} refetchChannels={refetchChannels} channelId={channel.id ?? ""} />
		));
	}, [canUseChannels, modals, channel.id, refetchChannels]);

	const leaveChannel = useLeaveChannel({
		origin: "LibraryPanel",
		viewer,
		channel,
		redirectToLibrary: shouldRedirect,
	});

	const { viewerLastSeenAt, lastPostedAt } = channel;
	const isUnseen = Boolean(viewerLastSeenAt && lastPostedAt && new Date(viewerLastSeenAt) < new Date(lastPostedAt));

	return (
		<div className={panelSectionStyles.wrapper}>
			<MenuLink
				{...restProps}
				innerRef={innerRef}
				to={`/library/c/${channel.id}/`}
				activeClassName={panelSectionStyles.active}
				data-test-id="channels-link"
				icon={<FontAwesomeIcon icon={channel.isPrivate ? faLock : faHashtag} fixedWidth />}
				iconTooltip={channel.name}
				unread={isUnseen}
			>
				<span title={channel.name} className={panelSectionStyles.title}>
					{channel.name}
				</span>
			</MenuLink>
			<Menu onOpenChange={setIsMenuOpen}>
				<Menu.Trigger asChild>
					<IconButton
						title="Options"
						icon={faEllipsisV}
						className={cx(panelSectionStyles.menuButton, { [panelSectionStyles.menuButtonVisible]: isMenuOpen })}
					/>
				</Menu.Trigger>
				<Menu.Content className={panelSectionStyles.menuContent}>
					<Menu.Item
						icon={canUseChannels ? faHashtag : faArrowAltCircleUp}
						onSelect={openChannelDetail}
						iconProps={{
							color: canUseChannels ? "inherit" : "var(--billingColor)",
						}}
					>
						Channel Details
					</Menu.Item>
					<Menu.Separator />
					<Menu.Item destructive icon={faDoorClosed} onSelect={leaveChannel}>
						Leave channel
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
}

interface ChannelsSectionProps {
	viewer: LibraryPanelChannelLink_viewer$key;
	items: ReadonlyArray<Channel | null>;
	onBrowseChannelsModal: () => void;
}

function ChannelsSection({ viewer, items, onBrowseChannelsModal }: ChannelsSectionProps): React.ReactElement {
	const [channels, setChannels] = useState<Channel[]>([]);
	useEffect(() => {
		setChannels(items as Channel[]);
	}, [items]);

	React.useEffect(() => {
		async function onWebsocketMessage(message: WebsocketMessage) {
			if (message.type !== "LAST_POSTED_AT_CHANGED") {
				return;
			}

			const data = message.data;

			commitLocalUpdate(environment, store => {
				const channelNode = store.get(data.channelId);
				if (channelNode) {
					channelNode.setValue(data.lastPostedAt, "lastPostedAt");
				}
			});
		}

		const socket = WebsocketClient.getInstance().getOrJoinGroup("channels.library", {});
		socket.on("message", onWebsocketMessage);

		return () => {
			if (socket) {
				socket.off("message", onWebsocketMessage);
				socket.close();
			}
		};
	}, []);

	const reorder = (list: Channel[], startIndex: number, endIndex: number) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		const orderCount: { [key: number]: number } = {};
		const changedChannels = [];

		for (let i = 0; i < result.length; i++) {
			const item = result[i];

			if (item.order && !orderCount[item.order]) {
				orderCount[item.order] = 1;
			} else if (item.order) {
				orderCount[item.order]++;
			}

			if (item.order !== i || orderCount[item.order] > 1) {
				changedChannels.push({ id: item.id, order: i });
			}
		}

		// we filter out the channels that the order has not changed
		const onlyChangedResults = [];
		for (let i = 0; i < changedChannels.length; i++) {
			if (changedChannels[i].id !== list[i].id) {
				onlyChangedResults.push(changedChannels[i]);
			}
		}

		return { newChannels: result, changedChannels: onlyChangedResults };
	};

	const onDragEnd = React.useCallback(
		(result: DropResult) => {
			if (!result.destination) {
				return;
			}

			const { newChannels, changedChannels } = reorder(channels, result.source.index, result.destination.index);

			setChannels(newChannels);

			if (changedChannels.length > 0) {
				commitMutation<LibraryPanelReorderChannelMutation>(environment, {
					mutation: reorderChannelMutation,
					variables: { changedChannels: changedChannels as ChangedChannels[] },
					onCompleted: response => {
						if (!response.reorderChannel?.ok) {
							toast.error("Channel reordering failed.");
						}
					},
				});
			}
		},
		[channels],
	);

	return channels.length > 0 ? (
		<>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="channels">
					{provided => (
						<div {...provided.droppableProps} ref={provided.innerRef}>
							{channels.map((channel, index) => (
								<Draggable key={channel.id} draggableId={channel.id ?? ""} index={index}>
									{provided => (
										<div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
											<ChannelLink key={channel.id} channel={channel} viewer={viewer} />
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			<Button
				variant="tertiary"
				size="slim"
				className={cx(panelSectionStyles.menuLink, styles.addChannelsButton)}
				onClick={onBrowseChannelsModal}
			>
				<FontAwesomeIcon icon={faPlus} fixedWidth />
				<span title="Add channels" className={panelSectionStyles.title}>
					Add channels
				</span>
			</Button>
		</>
	) : (
		<p className={styles.emptyPanelSection}>
			You have yet to join any channels.
			<ButtonLink className={styles.browseChannelLink} onClick={onBrowseChannelsModal}>
				Add channels
			</ButtonLink>
			.
		</p>
	);
}

export default function LibraryPanel() {
	const { viewer } = useLazyLoadQuery<QueryType>(
		graphql`
			query LibraryPanelQuery($orderBy: ChannelOrdering) {
				viewer {
					user {
						isManager
					}
					channels(memberOnly: true, excludePersonal: true, orderBy: $orderBy) {
						id
						name
						isWorkspaceWide
						isPersonal
						isMember
						isPrivate
						isMyMeetings
						isFavorites
						viewerLastSeenAt
						lastPostedAt
					}
					account {
						billingAccount {
							hasAccessToMyTeamPseudoChannel: hasAccessToFeature(feature: my_team_pseudo_channel)
						}
					}
					...RecordWithUrlButton_viewer
					...LibraryHooksUseOpenChannelsModal_viewer
					...LibraryPanelChannelLink_viewer
					...AddToLibraryMenu_viewer
				}
			}
		`,
		{ orderBy: "ORDER_ASC" },
		{ fetchPolicy: "store-and-network", fetchKey: useFetchKeyForEvent("Channels.refetch") },
	);

	const { channels, user } = viewer;

	const openChannelsModal = useOpenChannelsModal(viewer, "Library Panel");
	const uploadsEnabled = useIsUploadsEnabled();
	const shouldRenderMyTeamPseudoChannelLink = useIsMyTeamPseudoChannelEnabled() && user.isManager;
	const isMyTeamPseudoChannelPaywalled = !viewer.account.billingAccount.hasAccessToMyTeamPseudoChannel;
	const location = useLocation();
	const currentChannel = channels.find(channel => location.pathname.startsWith(`/library/c/${channel.id}/`));
	const uploadDefaultChannels = currentChannel
		? [
				{
					label: currentChannel.name,
					value: currentChannel.id,
				},
			]
		: null;

	return (
		<div className={panelStyles.panel}>
			<div className={panelStyles.panelHeader}>
				<h1 className={panelStyles.panelTitle}>Library</h1>
				<AddToLibraryMenu
					viewer={viewer}
					defaultChannels={uploadDefaultChannels}
					analyticsLocationDescription={"Library panel"}
					align="start"
				>
					<IconButton variant="primary" icon={faPlus} />
				</AddToLibraryMenu>
			</div>
			<div className={panelStyles.main}>
				<div className={cx(panelSectionStyles.panelSection, panelSectionStyles.panelSectionPrimary)}>
					<div className={panelSectionStyles.panelSectionContent}>
						<PrimaryNavLink exact to="/library/">
							<FontAwesomeIcon icon={faCalendar} fixedWidth />
							All meetings
						</PrimaryNavLink>
						<PrimaryNavLink exact to="/library/my-meetings">
							<FontAwesomeIcon icon={faCalendarCircleUser} fixedWidth />
							My meetings
						</PrimaryNavLink>
						<PrimaryNavLink to="/library/shared-with-me/">
							<FontAwesomeIcon icon={faUserFriends} fixedWidth />
							Shared with me
						</PrimaryNavLink>
						<PrimaryNavLink to="/library/watch-later/">
							<FontAwesomeIcon icon={faClock} fixedWidth />
							Watch later
						</PrimaryNavLink>
						{shouldRenderMyTeamPseudoChannelLink && (
							<PaywalledPrimaryNavLink
								to="/library/my-team/"
								isPaywalled={isMyTeamPseudoChannelPaywalled}
								feature="my_team_pseudo_channel"
								icon={faHandshake}
							>
								External <InlineLabel className={styles.myTeamLabel} text="My team" />
							</PaywalledPrimaryNavLink>
						)}
						{uploadsEnabled && (
							<PrimaryNavLink to="/library/uploads/">
								<FontAwesomeIcon icon={faArrowToTop} fixedWidth />
								Uploads
							</PrimaryNavLink>
						)}
					</div>
				</div>
				<div>
					<PanelSection
						title="My Channels"
						icon={faHashtag}
						plusButtonTooltip="Add channels"
						isCollapsable={false}
						onPlusClick={openChannelsModal}
					>
						<ChannelsSection items={channels ?? []} onBrowseChannelsModal={openChannelsModal} viewer={viewer} />
					</PanelSection>
				</div>
			</div>
			<div className={styles.recordNowButton}>
				<RecordWithUrlButton viewer={viewer} analyticsLocationDescription="Library panel" variant="secondary">
					<FontAwesomeIcon icon={faVideo} fixedWidth /> Record now
				</RecordWithUrlButton>
			</div>
		</div>
	);
}
